import React from 'react';
import type { FloorPlanStair } from '../../../types';
import { stairDrawing } from './floorPlanStairs';

interface GlyphProps {
  catalogId: string;
  widthIn: number;
  depthIn: number;
  stair?: FloorPlanStair;
}

function vehicleGlyph(catalogId: string, w: number, d: number, stroke: number) {
  const cab =
    catalogId === 'forklift' ? 0.38 :
    catalogId === 'trailer' ? 0.12 :
    catalogId === 'car' || catalogId === 'suv' ? 0.28 :
    catalogId === 'pickup' || catalogId === 'van' ? 0.3 :
    0.22;
  const cabX = w * cab;
  const bodyY = d * 0.16;
  const bodyH = d * 0.68;
  const wheel = Math.min(d * 0.11, w * 0.035);
  const wheelXs =
    catalogId === 'forklift' ? [w * 0.22, w * 0.78] :
    catalogId === 'car' || catalogId === 'suv' ? [w * 0.22, w * 0.78] :
    [w * 0.16, w * 0.42, w * 0.78];
  return (
    <g fill="none" stroke="currentColor" strokeWidth={stroke}>
      <rect x={0} y={bodyY} width={w} height={bodyH} rx={d * 0.1} />
      {catalogId !== 'trailer' && (
        <>
          <line x1={cabX} y1={bodyY} x2={cabX} y2={bodyY + bodyH} />
          <rect x={stroke} y={d * 0.28} width={Math.max(cabX - stroke * 2, 2)} height={d * 0.44} rx={1} />
        </>
      )}
      {(catalogId === 'box-truck' || catalogId === 'van') && (
        <rect x={cabX} y={bodyY} width={w - cabX} height={bodyH} />
      )}
      {catalogId === 'flatbed' && (
        <line x1={cabX} y1={d * 0.5} x2={w - stroke} y2={d * 0.5} />
      )}
      {catalogId === 'dump-truck' && (
        <path d={`M ${cabX} ${bodyY + bodyH} L ${cabX + w * 0.08} ${bodyY} H ${w - stroke} V ${bodyY + bodyH} Z`} />
      )}
      {catalogId === 'crane-truck' && (
        <>
          <line x1={cabX + w * 0.08} y1={d * 0.5} x2={w * 0.92} y2={d * 0.22} />
          <line x1={w * 0.92} y1={d * 0.22} x2={w * 0.86} y2={d * 0.18} />
          <line x1={w * 0.92} y1={d * 0.22} x2={w * 0.86} y2={d * 0.28} />
          <line x1={w * 0.12} y1={d * 0.08} x2={w * 0.12} y2={d * 0.92} />
          <line x1={w * 0.72} y1={d * 0.08} x2={w * 0.72} y2={d * 0.92} />
        </>
      )}
      {catalogId === 'forklift' && (
        <>
          <line x1={w} y1={d * 0.32} x2={w + w * 0.02} y2={d * 0.32} />
          <line x1={w * 0.92} y1={d * 0.32} x2={w} y2={d * 0.32} />
          <line x1={w * 0.92} y1={d * 0.68} x2={w} y2={d * 0.68} />
        </>
      )}
      {catalogId === 'pickup' && (
        <line x1={cabX + w * 0.08} y1={bodyY} x2={cabX + w * 0.08} y2={bodyY + bodyH} />
      )}
      {wheelXs.map((x) => (
        <g key={x}>
          <rect x={x - wheel} y={d * 0.04} width={wheel * 2} height={wheel} rx={wheel * 0.3} />
          <rect x={x - wheel} y={d * 0.96 - wheel} width={wheel * 2} height={wheel} rx={wheel * 0.3} />
        </g>
      ))}
    </g>
  );
}

/** Top-down architectural symbols in local inch space (0,0 → width × depth). */
const FloorPlanGlyph: React.FC<GlyphProps> = ({ catalogId, widthIn: w, depthIn: d, stair }) => {
  const drawing = stairDrawing({ catalogId, widthIn: w, depthIn: d, stair });
  if (drawing) {
    const stroke = Math.max(0.45, Math.min(w, d) * 0.014);
    return (
      <g fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinejoin="miter">
        {drawing.outline && <path d={drawing.outline} />}
        {drawing.circles.map((circle, i) => (
          <circle key={`c-${i}`} cx={circle.cx} cy={circle.cy} r={circle.r} />
        ))}
        {drawing.treads.map((line, i) => (
          <line key={`t-${i}`} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} />
        ))}
        {drawing.arrowPath && <path d={drawing.arrowPath} />}
        {drawing.arrows.map((line, i) => (
          <line key={`a-${i}`} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} />
        ))}
        {drawing.labels.map((label, i) => (
          <text
            key={`l-${i}`}
            x={label.x}
            y={label.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="currentColor"
            stroke="none"
            fontSize={label.font}
            fontWeight={700}
          >
            {label.text}
          </text>
        ))}
      </g>
    );
  }
  const stroke = Math.max(0.7, Math.min(w, d) * 0.035);
  switch (catalogId) {
    case 'sofa':
    case 'loveseat':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} rx={d * 0.12} />
          <rect x={stroke} y={d * 0.42} width={w - stroke * 2} height={d * 0.52} rx={d * 0.08} />
          <line x1={w * 0.33} y1={d * 0.42} x2={w * 0.33} y2={d - stroke} />
          <line x1={w * 0.66} y1={d * 0.42} x2={w * 0.66} y2={d - stroke} />
        </g>
      );
    case 'armchair':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} rx={d * 0.16} />
          <rect x={w * 0.12} y={d * 0.4} width={w * 0.76} height={d * 0.5} rx={d * 0.1} />
        </g>
      );
    case 'dining-table':
    case 'coffee-table':
    case 'desk':
    case 'island':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} rx={1.5} />
          {catalogId === 'dining-table' && (
            <>
              <circle cx={w * 0.22} cy={d * 0.5} r={Math.min(w, d) * 0.12} />
              <circle cx={w * 0.5} cy={d * 0.5} r={Math.min(w, d) * 0.12} />
              <circle cx={w * 0.78} cy={d * 0.5} r={Math.min(w, d) * 0.12} />
            </>
          )}
        </g>
      );
    case 'bed-queen':
    case 'bed-king':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <rect x={stroke} y={stroke} width={w * 0.42 - stroke} height={d * 0.28} />
          <rect x={w * 0.52} y={stroke} width={w * 0.42} height={d * 0.28} />
          <line x1={0} y1={d * 0.36} x2={w} y2={d * 0.36} />
        </g>
      );
    case 'nightstand':
    case 'base-cabinet':
    case 'dishwasher':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <line x1={w * 0.2} y1={d * 0.5} x2={w * 0.8} y2={d * 0.5} />
        </g>
      );
    case 'fridge':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <line x1={w * 0.5} y1={0} x2={w * 0.5} y2={d} />
          <circle cx={w * 0.22} cy={d * 0.5} r={stroke * 1.2} fill="currentColor" />
          <circle cx={w * 0.78} cy={d * 0.5} r={stroke * 1.2} fill="currentColor" />
        </g>
      );
    case 'range':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <circle cx={w * 0.3} cy={d * 0.35} r={Math.min(w, d) * 0.14} />
          <circle cx={w * 0.7} cy={d * 0.35} r={Math.min(w, d) * 0.14} />
          <circle cx={w * 0.3} cy={d * 0.7} r={Math.min(w, d) * 0.12} />
          <circle cx={w * 0.7} cy={d * 0.7} r={Math.min(w, d) * 0.12} />
        </g>
      );
    case 'sink':
    case 'vanity':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <ellipse cx={w * 0.5} cy={d * 0.52} rx={w * 0.32} ry={d * 0.28} />
          <circle cx={w * 0.5} cy={d * 0.22} r={stroke * 1.1} fill="currentColor" />
        </g>
      );
    case 'toilet':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={w * 0.18} y={0} width={w * 0.64} height={d * 0.38} rx={1} />
          <ellipse cx={w * 0.5} cy={d * 0.68} rx={w * 0.38} ry={d * 0.28} />
        </g>
      );
    case 'tub':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} rx={d * 0.22} />
          <ellipse cx={w * 0.5} cy={d * 0.5} rx={w * 0.38} ry={d * 0.28} />
        </g>
      );
    case 'shower':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <line x1={0} y1={0} x2={w} y2={d} />
          <circle cx={w * 0.22} cy={d * 0.22} r={stroke * 1.4} />
        </g>
      );
    case 'shop-cabinet-24':
    case 'shop-cabinet-36':
    case 'shop-cabinet-48': {
      const doors = catalogId === 'shop-cabinet-24' ? 1 : catalogId === 'shop-cabinet-36' ? 2 : 3;
      const doorW = w / doors;
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          {Array.from({ length: doors - 1 }, (_, i) => (
            <line key={i} x1={doorW * (i + 1)} y1={0} x2={doorW * (i + 1)} y2={d} />
          ))}
          {Array.from({ length: doors }, (_, i) => (
            <circle
              key={`h-${i}`}
              cx={doorW * i + doorW * (i % 2 === 0 ? 0.82 : 0.18)}
              cy={d * 0.5}
              r={stroke * 1.1}
              fill="currentColor"
            />
          ))}
        </g>
      );
    }
    case 'tool-chest':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <line x1={stroke} y1={d * 0.28} x2={w - stroke} y2={d * 0.28} />
          <line x1={stroke} y1={d * 0.5} x2={w - stroke} y2={d * 0.5} />
          <line x1={stroke} y1={d * 0.72} x2={w - stroke} y2={d * 0.72} />
          <circle cx={w * 0.5} cy={d * 0.14} r={stroke} fill="currentColor" />
        </g>
      );
    case 'flammable-cabinet':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <line x1={w * 0.18} y1={d * 0.18} x2={w * 0.82} y2={d * 0.82} />
          <line x1={w * 0.82} y1={d * 0.18} x2={w * 0.18} y2={d * 0.82} />
        </g>
      );
    case 'welder':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={w * 0.08} y={d * 0.12} width={w * 0.84} height={d * 0.58} rx={1} />
          <rect x={w * 0.22} y={d * 0.22} width={w * 0.36} height={d * 0.32} />
          <circle cx={w * 0.28} cy={d * 0.86} r={d * 0.1} />
          <circle cx={w * 0.72} cy={d * 0.86} r={d * 0.1} />
        </g>
      );
    case 'compressor':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={w * 0.06} y={d * 0.28} width={w * 0.72} height={d * 0.48} rx={d * 0.24} />
          <rect x={w * 0.7} y={d * 0.18} width={w * 0.24} height={d * 0.64} rx={1} />
          <circle cx={w * 0.22} cy={d * 0.86} r={d * 0.1} />
          <circle cx={w * 0.58} cy={d * 0.86} r={d * 0.1} />
        </g>
      );
    case 'plasma-table':
    case 'cnc-router':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <rect x={stroke} y={d * 0.18} width={w - stroke * 2} height={d * 0.64} />
          <line x1={w * 0.18} y1={d * 0.12} x2={w * 0.18} y2={d * 0.88} />
          <line x1={w * 0.18} y1={d * 0.38} x2={w * 0.92} y2={d * 0.38} />
          <rect x={w * 0.42} y={d * 0.3} width={w * 0.16} height={d * 0.16} />
        </g>
      );
    case 'welding-table':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <line x1={w * 0.18} y1={0} x2={w * 0.18} y2={d} />
          <line x1={w * 0.36} y1={0} x2={w * 0.36} y2={d} />
          <line x1={w * 0.54} y1={0} x2={w * 0.54} y2={d} />
          <line x1={w * 0.72} y1={0} x2={w * 0.72} y2={d} />
          <line x1={w * 0.9} y1={0} x2={w * 0.9} y2={d} />
        </g>
      );
    case 'workbench':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <rect x={0} y={0} width={w * 0.12} height={d * 0.42} />
          <circle cx={w * 0.28} cy={d * 0.28} r={stroke * 0.9} />
          <circle cx={w * 0.5} cy={d * 0.28} r={stroke * 0.9} />
          <circle cx={w * 0.72} cy={d * 0.28} r={stroke * 0.9} />
        </g>
      );
    case 'table-saw':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <line x1={w * 0.5} y1={d * 0.18} x2={w * 0.5} y2={d * 0.82} />
          <line x1={w * 0.12} y1={d * 0.22} x2={w * 0.12} y2={d * 0.78} />
        </g>
      );
    case 'band-saw':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <circle cx={w * 0.38} cy={d * 0.32} r={Math.min(w, d) * 0.2} />
          <circle cx={w * 0.38} cy={d * 0.72} r={Math.min(w, d) * 0.16} />
          <line x1={w * 0.38} y1={d * 0.12} x2={w * 0.38} y2={d * 0.88} />
        </g>
      );
    case 'miter-saw':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={d * 0.28} width={w} height={d * 0.44} />
          <rect x={w * 0.36} y={d * 0.12} width={w * 0.28} height={d * 0.76} />
          <line x1={0} y1={d * 0.5} x2={w} y2={d * 0.5} />
        </g>
      );
    case 'drill-press':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <circle cx={w * 0.5} cy={d * 0.58} r={Math.min(w, d) * 0.22} />
          <rect x={w * 0.38} y={0} width={w * 0.24} height={d * 0.28} />
        </g>
      );
    case 'planer':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={d * 0.22} width={w} height={d * 0.56} />
          <rect x={w * 0.28} y={d * 0.08} width={w * 0.44} height={d * 0.84} />
          <line x1={w * 0.08} y1={d * 0.5} x2={w * 0.92} y2={d * 0.5} />
        </g>
      );
    case 'dust-collector':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <circle cx={w * 0.38} cy={d * 0.5} r={Math.min(w, d) * 0.32} />
          <rect x={w * 0.62} y={d * 0.22} width={w * 0.32} height={d * 0.56} rx={1} />
        </g>
      );
    case 'lumber-rack':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <line x1={w * 0.2} y1={0} x2={w * 0.2} y2={d} />
          <line x1={w * 0.4} y1={0} x2={w * 0.4} y2={d} />
          <line x1={w * 0.6} y1={0} x2={w * 0.6} y2={d} />
          <line x1={w * 0.8} y1={0} x2={w * 0.8} y2={d} />
        </g>
      );
    case 'pallet':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <line x1={0} y1={d * 0.22} x2={w} y2={d * 0.22} />
          <line x1={0} y1={d * 0.5} x2={w} y2={d * 0.5} />
          <line x1={0} y1={d * 0.78} x2={w} y2={d * 0.78} />
        </g>
      );
    case 'gas-cylinders':
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
          <circle cx={w * 0.28} cy={d * 0.5} r={Math.min(w, d) * 0.22} />
          <circle cx={w * 0.72} cy={d * 0.5} r={Math.min(w, d) * 0.22} />
        </g>
      );
    case 'car':
    case 'suv':
    case 'pickup':
    case 'van':
    case 'box-truck':
    case 'flatbed':
    case 'dump-truck':
    case 'crane-truck':
    case 'forklift':
    case 'trailer':
      return vehicleGlyph(catalogId, w, d, stroke);
    case 'north-arrow': {
      const cx = w / 2;
      const cy = d / 2;
      const r = Math.min(w, d) * 0.42;
      const font = Math.min(w, d) * 0.18;
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <circle cx={cx} cy={cy} r={r} />
          <text
            x={cx}
            y={cy - r * 0.62}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="currentColor"
            stroke="none"
            fontSize={font}
            fontWeight={700}
          >
            N
          </text>
          <path
            d={`M ${cx} ${cy - r * 0.42} L ${cx + r * 0.32} ${cy + r * 0.08} L ${cx} ${cy - r * 0.08} L ${cx - r * 0.32} ${cy + r * 0.08} Z`}
            fill="currentColor"
            stroke="none"
          />
          <path
            d={`M ${cx} ${cy + r * 0.72} L ${cx + r * 0.22} ${cy + r * 0.12} L ${cx} ${cy + r * 0.28} L ${cx - r * 0.22} ${cy + r * 0.12} Z`}
          />
        </g>
      );
    }
    default:
      return (
        <g fill="none" stroke="currentColor" strokeWidth={stroke}>
          <rect x={0} y={0} width={w} height={d} />
        </g>
      );
  }
};

export default FloorPlanGlyph;
